import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import content from '@/data/homepageContent.json';
import { ymGoalThen } from '@/lib/analytics';
import { THEME } from '@/lib/themePalette';

/*
 * Блок об авторе и общие классы кнопок.
 *
 * Жили в HomeSections.jsx вместе со всей главной. Переехали в отдельный
 * файл, когда блок понадобился на /consultation: импорт HomeSections
 * утянул бы в чанк страницы консультации всю главную — витрину тарифов,
 * отзывы, FAQ, авторизацию и аналитику. Разметка при этом ОДНА: и главная,
 * и категории, и консультация рендерят этот же компонент.
 *
 * HomeSections реэкспортирует ExpertBlock и CTA_BTN_CLASS, поэтому все
 * прежние импорты продолжают работать без правок.
 */

// Единый цвет всех CTA-кнопок: accent-cta-500 (совпадает с «РАССЧИТАТЬ КАРТУ»).
export const CTA_BTN_CLASS =
  'bg-accent-cta-500 hover:bg-accent-cta-600 text-white font-semibold rounded-lg';
// Светло-фиолетовый фон для зонирующих секций (примерно на 50% светлее accent-cta-500).
export const TINT_BG_CLASS = 'bg-accent-cta-100/85';

// Тематические SVG-иконки для достижений эксперта (в брендовой палитре)
const EXPERT_ICONS = [
  // Эксперт ТВ — TV / broadcast
  ({ cls }) => (
    <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="13" rx="2" />
      <path d="M7 3l5 3 5-3" />
      <circle cx="17" cy="12.5" r="1" fill="currentColor" />
    </svg>
  ),
  // Автор методики ASTROfeel — открытая книга со звездой
  ({ cls }) => (
    <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 5v14l8-3 8 3V5l-8 3-8-3z" />
      <path d="M12 8v6" />
      <path d="M12 5l1 1.6 1.7.2-1.2 1.2.3 1.7L12 8.9l-1.5.8.3-1.7L9.6 6.8l1.7-.2L12 5z" fill="currentColor" stroke="none" />
    </svg>
  ),
  // Тысячи натальных карт — компактный зодиакальный круг
  ({ cls }) => (
    <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6L5.6 18.4" opacity="0.55" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  ),
  // Практикующий астролог — полумесяц со звездой
  ({ cls }) => (
    <svg viewBox="0 0 24 24" className={cls} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" />
      <path d="M17 3l.9 2 2.1.3-1.5 1.4.4 2-1.9-1-1.9 1 .4-2L14 5.3l2.1-.3L17 3z" fill="currentColor" stroke="none" />
    </svg>
  ),
];

// ═══════════════════════════════════════════════════════════════════════
//  Expert Block — Светлана. Спокойный блок доверия на белом фоне.
//  Фото — компактный акцент. Кнопки справа от списка достижений.
//
//  showConsultation включает кнопку «Заказать консультацию». По умолчанию
//  выключена: этот же блок рендерят страницы категорий (/love, /finance,
//  /success, /forecast), а кнопку просили поставить на главной. Чтобы
//  показать её и на категориях — достаточно передать проп там же.
// ═══════════════════════════════════════════════════════════════════════
// Фото эксперта по теме сайта. Механизм тот же, что у иконок в src/index.js:
// папка public/theme/<тема>/ и переменная сборки REACT_APP_THEME, новых не
// заводим. Темы, которых здесь нет (в том числе sky), берут фото из
// homepageContent.json — поэтому на астрофил.рф картинка прежняя.
const EXPERT_PHOTO_BY_THEME = {
  violet: '/theme/violet/expert.webp',
};

export function ExpertBlock({ showConsultation = false }) {
  const e = content.expert;
  const photo = EXPERT_PHOTO_BY_THEME[THEME] || e.photo;
  const navigate = useNavigate();
  const consultPath = e.consultPath || '/consultation';
  return (
    <section className="bg-white py-3 md:py-4 px-4" data-testid="expert-block">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
          {/* Фото — без фиксированного aspect-контейнера: тот давал видимые
              голубые поля сверху/снизу везде, где реальные пропорции фото не
              совпадали с 3/4. Теперь картинка просто занимает ширину блока
              на своих собственных пропорциях — полей нет. +50% к ширине. */}
          <div className="w-[330px] md:w-[420px] flex-shrink-0" data-testid="expert-photo">
            <img width="1511" height="1041"
              src={photo}
              alt={e.name}
              className="w-full h-auto rounded-md"
              loading="lazy"
              decoding="async"
            />
          </div>

          {/* Инфо — спокойный размер, кнопка справа от списка */}
          <div className="flex-1 min-w-0 w-full">
            <h2
              className="text-base md:text-lg font-heading font-bold text-slate-900 leading-tight"
              data-testid="expert-name"
            >
              {e.name}
            </h2>
            <p className="text-sm text-slate-600 mt-0.5 mb-2.5">{e.bio}</p>
            {/* Достижения + CTA на одной горизонтальной линии */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <ul className="flex-1 flex flex-col gap-1.5">
                {e.credits.map((c, i) => {
                  const Icon = EXPERT_ICONS[i % EXPERT_ICONS.length];
                  return (
                    <li
                      key={i}
                      className="flex items-center gap-2 text-sm text-slate-700"
                      data-testid={`expert-credit-${i}`}
                    >
                      <Icon cls="w-5 h-5 text-accent-cta-500 flex-shrink-0" />
                      <span className="leading-snug">{c}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="flex-shrink-0 w-full md:w-auto">
                {/* Кнопки в столбик на любом экране: сверху «Об авторе», под
                    ней «Заказать консультацию». Ширина у обеих одна — колонка
                    сама по себе шириной с самую широкую кнопку, а stretch
                    растягивает вторую до неё. */}
                <div className="flex flex-col items-stretch gap-2">
                  <Button
                    onClick={() => navigate(e.ctaPath || '/about')}
                    variant="outline"
                    className="h-12 px-7 text-sm font-semibold border-2 border-accent-cta-300 text-accent-cta-600 hover:bg-accent-cta-50 hover:text-accent-cta-700"
                    data-testid="expert-cta"
                  >
                    {e.ctaLabel || 'Об авторе'}
                  </Button>
                  {showConsultation && (
                    <Button
                      onClick={() =>
                        ymGoalThen('consultation_click', { place: 'expert_block' }, () =>
                          navigate(consultPath),
                        )
                      }
                      className={`${CTA_BTN_CLASS} h-12 px-7 text-sm`}
                      data-testid="expert-consultation-cta"
                    >
                      {e.consultLabel || 'Заказать консультацию'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
